import { useCallback, useEffect, useMemo, useState } from "react";

import { triggerBirthdayAutomation } from "@/services/crm/triggerClinicalAutomation";
import { useMessagingTemplatesForSend } from "src-next/modules/crm-clinical/messaging";
import { automationService } from "src-next/modules/crm-clinical/automation/services/automationService";
import type { AutomationItem } from "src-next/modules/crm-clinical/automation/types";
import {
  filterBirthdayToday,
  filterPatientsWithPhone,
} from "../aggregations";
import { useClinicalCrmCommunicationsData } from "../hooks/useClinicalCrmCommunicationsData";
import { mapPatientEntriesToCrmPatientSummaries } from "../helpers/mapFhirPatientToCrmPatientSummary";
import { resolveBirthdayBatchTaskForToday } from "../helpers/resolveBirthdayBatchTaskForToday";
import {
  deriveBatchTaskSendStatus,
  formatBatchReceivedTotal,
  formatBatchFailuresCount,
} from "../helpers/formatBatchTaskDisplay";
import type { CrmPatientSummary, FluxZapBatchTaskView } from "../types";

export interface UseBirthdayDashboardStateParams {
  organizationId?: string;
  practitionerId?: string;
  requesterReference?: string;
  patientEntries?: fhir5.BundleEntry<fhir5.Patient>[];
  patientOrganizationLoading?: boolean;
  /** Incrementa para recarregar comunicações após envio. */
  refreshSignal?: number;
}

export type BirthdayDispatchStatus =
  | "notDispatched"
  | "inProgress"
  | "completed"
  | "error";

export interface UseBirthdayDashboardStateResult {
  shouldShowAlert: boolean;
  todayBirthdays: CrmPatientSummary[];
  todayBirthdaysWithPhone: CrmPatientSummary[];
  birthdayAutomation: AutomationItem | null;
  isAutomationReady: boolean;
  todayBatchTask: FluxZapBatchTaskView | null;
  dispatchStatus: BirthdayDispatchStatus;
  dispatchStatusLabel: string;
  receivedCount: string;
  failuresCount: string;
  canTriggerDispatch: boolean;
  triggerDisabledReason: string | null;
  isLoading: boolean;
  isTriggering: boolean;
  communications: ReturnType<typeof useClinicalCrmCommunicationsData>["communications"];
  allPatients: CrmPatientSummary[];
  patientLookup: Map<string, CrmPatientSummary>;
  refetchCommunications: () => void;
  triggerDispatch: () => Promise<void>;
}

/**
 * Estado do alerta de aniversariantes no dashboard profissional.
 * Agrega pacientes, automação birthday e Task de lote do dia.
 */
export function useBirthdayDashboardState(
  params: UseBirthdayDashboardStateParams,
): UseBirthdayDashboardStateResult {
  const {
    organizationId,
    practitionerId,
    requesterReference = "",
    patientEntries,
    patientOrganizationLoading = false,
    refreshSignal = 0,
  } = params;

  const [birthdayAutomation, setBirthdayAutomation] =
    useState<AutomationItem | null>(null);
  const [automationLoading, setAutomationLoading] = useState(false);
  const [isTriggering, setIsTriggering] = useState(false);
  const [localRefresh, setLocalRefresh] = useState(0);

  const allPatients = useMemo(
    () => mapPatientEntriesToCrmPatientSummaries(patientEntries),
    [patientEntries],
  );

  const patientLookup = useMemo(() => {
    const map = new Map<string, CrmPatientSummary>();
    for (const p of allPatients) {
      map.set(p.patientId, p);
    }
    return map;
  }, [allPatients]);

  const todayBirthdays = useMemo(
    () => filterBirthdayToday(allPatients, new Date()),
    [allPatients],
  );

  const todayBirthdaysWithPhone = useMemo(
    () => filterPatientsWithPhone(todayBirthdays),
    [todayBirthdays],
  );

  const {
    communications,
    batchTasks,
    isLoading: communicationsLoading,
    refetch,
  } = useClinicalCrmCommunicationsData(
    organizationId,
    refreshSignal + localRefresh,
  );

  const { templates: messagingTemplates } = useMessagingTemplatesForSend({
    organizationId,
  });

  useEffect(() => {
    const orgId = organizationId?.trim();
    const practId = practitionerId?.trim();
    if (!orgId || !practId) {
      setBirthdayAutomation(null);
      return;
    }

    let cancelled = false;
    setAutomationLoading(true);

    void automationService
      .listByContext({
        context: { organizationId: orgId, practitionerId: practId },
      })
      .then((items) => {
        if (cancelled) return;
        setBirthdayAutomation(items.find((item) => item.id === "birthday") ?? null);
      })
      .catch(() => {
        if (!cancelled) setBirthdayAutomation(null);
      })
      .finally(() => {
        if (!cancelled) setAutomationLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId, practitionerId, localRefresh]);

  const todayBatchTask = useMemo(
    () =>
      resolveBirthdayBatchTaskForToday({
        batchTasks,
        messageTemplateId: birthdayAutomation?.messageTemplateId,
        messagingTemplates,
      }),
    [batchTasks, birthdayAutomation?.messageTemplateId, messagingTemplates],
  );

  const dispatchStatus = useMemo((): BirthdayDispatchStatus => {
    if (!todayBatchTask) return "notDispatched";
    const sendStatus = deriveBatchTaskSendStatus(todayBatchTask);
    if (sendStatus.kind === "inProgress") return "inProgress";
    if (sendStatus.kind === "error") return "error";
    return "completed";
  }, [todayBatchTask]);

  const dispatchStatusLabel = useMemo(() => {
    if (dispatchStatus === "notDispatched") return "Não disparado hoje";
    if (dispatchStatus === "inProgress") return "Disparo em andamento";
    if (dispatchStatus === "error") return "Disparo com falhas";
    return "Disparo concluído hoje";
  }, [dispatchStatus]);

  const receivedCount = todayBatchTask
    ? formatBatchReceivedTotal(todayBatchTask)
    : "0";
  const failuresCount = todayBatchTask
    ? formatBatchFailuresCount(todayBatchTask)
    : "0";

  const isAutomationReady = Boolean(
    birthdayAutomation?.isConfigured && birthdayAutomation?.isEnabled,
  );

  const triggerDisabledReason = useMemo(() => {
    if (!isAutomationReady) {
      if (!birthdayAutomation?.isConfigured) {
        return "Configure e habilite a automação Aniversariantes no CRM.";
      }
      return "A automação Aniversariantes está desabilitada.";
    }
    if (todayBirthdaysWithPhone.length === 0) {
      return todayBirthdays.length > 0
        ? "Nenhum aniversariante hoje possui telefone cadastrado."
        : "Não há aniversariantes hoje para disparo automático.";
    }
    if (dispatchStatus === "inProgress") {
      return "O disparo de hoje ainda está em andamento.";
    }
    if (dispatchStatus === "completed") {
      return "As mensagens de aniversário já foram disparadas hoje.";
    }
    return null;
  }, [
    birthdayAutomation?.isConfigured,
    dispatchStatus,
    isAutomationReady,
    todayBirthdays.length,
    todayBirthdaysWithPhone.length,
  ]);

  const canTriggerDispatch =
    isAutomationReady &&
    todayBirthdaysWithPhone.length > 0 &&
    dispatchStatus !== "inProgress" &&
    dispatchStatus !== "completed" &&
    !isTriggering;

  const triggerDispatch = useCallback(async () => {
    const orgId = organizationId?.trim();
    const practId = practitionerId?.trim();
    if (!orgId || !practId || !canTriggerDispatch) return;

    setIsTriggering(true);
    try {
      await triggerBirthdayAutomation({
        organizationId: orgId,
        practitionerId: practId,
        requesterReference: requesterReference || null,
        allPatients,
      });
      setLocalRefresh((n) => n + 1);
      refetch();
    } finally {
      setIsTriggering(false);
    }
  }, [
    allPatients,
    canTriggerDispatch,
    organizationId,
    practitionerId,
    refetch,
    requesterReference,
  ]);

  const isLoading =
    patientOrganizationLoading || communicationsLoading || automationLoading;

  // Alerta sempre visível após carregar pacientes — mesmo sem aniversariantes hoje.
  const shouldShowAlert =
    !patientOrganizationLoading && Boolean(organizationId?.trim());

  return {
    shouldShowAlert,
    todayBirthdays,
    todayBirthdaysWithPhone,
    birthdayAutomation,
    isAutomationReady,
    todayBatchTask,
    dispatchStatus,
    dispatchStatusLabel,
    receivedCount,
    failuresCount,
    canTriggerDispatch,
    triggerDisabledReason,
    isLoading,
    isTriggering,
    communications,
    allPatients,
    patientLookup,
    refetchCommunications: refetch,
    triggerDispatch,
  };
}
